/**
 * @voltxampere/ui — Manhattan wire router (Sprint 13)
 *
 * Per ADR-0018. A simple L-shape router — the start endpoint, a
 * single corner, and the end endpoint. Two `posture`s pick which
 * axis the corner sits on:
 *
 *   `horizontal-first` — first segment runs along x (corner at
 *                        `(end.x, start.y)`)
 *   `vertical-first`   — first segment runs along y (corner at
 *                        `(start.x, end.y)`)
 *
 * Edge cases:
 *   - `start === end`           → 0 segments (no-op)
 *   - same x or same y          → 1 segment (straight line)
 *
 * The WireTool calls `inferPosture` with the first mouse
 * displacement after the user clicks the start terminal so the
 * default corner choice tracks the user's drag direction; Tab
 * toggles between the two postures (KiCad convention).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const EMPTY_SEGMENTS = Object.freeze([]);
export function routeManhattan(start, end, posture) {
    if (start.x === end.x && start.y === end.y) {
        return { segments: EMPTY_SEGMENTS, posture };
    }
    if (start.x === end.x || start.y === end.y) {
        return { segments: [{ start, end }], posture };
    }
    const corner = posture === "horizontal-first" ? { x: end.x, y: start.y } : { x: start.x, y: end.y };
    return {
        segments: [
            { start, end: corner },
            { start: corner, end },
        ],
        posture,
    };
}
/**
 * Choose a default `Posture` based on the first mouse
 * displacement from the start endpoint. The dominant axis wins;
 * a tie defaults to horizontal-first (matches the user's
 * left-to-right reading bias on schematic editors).
 */
export function inferPosture(start, currentMouse) {
    const dx = Math.abs(currentMouse.x - start.x);
    const dy = Math.abs(currentMouse.y - start.y);
    return dx >= dy ? "horizontal-first" : "vertical-first";
}
/** Convenience for `Tab` keybinding — toggle to the other posture. */
export function togglePosture(p) {
    return p === "horizontal-first" ? "vertical-first" : "horizontal-first";
}
//# sourceMappingURL=manhattan.js.map